import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Typography, 
  Box,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Snackbar
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { IApplicationState, RootActionCreators } from '../../../store';
import { FetchState } from '../../../entities/FetchState';
import { SavingMessage } from '../SavingMessage';
import { ErrorMessage } from '../ErrorMessage';

const useStyles = makeStyles((theme) => ({
  statusSection: {
    marginBottom: theme.spacing(3),
  },
  statusChip: {
    marginTop: theme.spacing(1),
    fontSize: '1rem',
    padding: theme.spacing(1),
  },
  warningBox: {
    marginTop: theme.spacing(2),
  },
  instructionsSection: {
    marginBottom: theme.spacing(3),
  },
  tokenInputSection: {
    marginBottom: theme.spacing(3),
  },
  tokenField: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

// Props from State
interface IPropsFromState {
  companyInfo: {
    state: FetchState;
    data: {
      name: string;
      erkenningsnummer: string;
      hasValidVaphJwtToken: boolean;
    };
    errorMessage: string;
  };
  saveTokenState: {
    state: FetchState;
    errorMessage: string;
  };
}

const mapStateToProps = (state: IApplicationState): IPropsFromState => ({
  companyInfo: {
    state: state.companies.companyInfo.CompanyInfo.state,
    data: {
      name: state.companies.companyInfo.CompanyInfo.data.name,
      erkenningsnummer: state.companies.companyInfo.CompanyInfo.data.erkenningsnummer,
      hasValidVaphJwtToken: state.companies.companyInfo.CompanyInfo.data.hasValidVaphJwtToken,
    },
    errorMessage: state.companies.companyInfo.CompanyInfo.errorMessage,
  },
  saveTokenState: {
    state: state.companies.companyInfo.SaveVaphToken.state,
    errorMessage: state.companies.companyInfo.SaveVaphToken.errorMessage,
  },
});

// Props from Dispatch
interface IPropsFromDispatch {
  loadCompanyInfo: () => void;
  saveVaphToken: (token: string) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IPropsFromDispatch => ({
  loadCompanyInfo: () => dispatch(RootActionCreators.Companies.companyInfo.FetchData()),
  saveVaphToken: (token: string) => dispatch(RootActionCreators.Companies.companyInfo.SaveVaphToken(token)),
});

// Component Props
interface IProps {}

// All Props
export type AllProps = IProps & IPropsFromState & IPropsFromDispatch;

export const VaphTokenComponent: React.FC<AllProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation('companies');
  const { t: tCommon } = useTranslation('common');
  
  const [tokenInput, setTokenInput] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [copiedSnackbar, setCopiedSnackbar] = useState(false);

  // Generate dynamic example values based on current date
  const today = new Date();
  const nextYear = new Date(today);
  nextYear.setFullYear(today.getFullYear() + 1);
  
  const formatDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  const exampleName = `dop database ${formatDate(today)}`;
  const exampleExpiry = formatDate(nextYear);
  const exampleDescription = `Synchronisatietoken DOP Databank gegenereerd op ${formatDate(today)}`;

  // Load company info on mount
  useEffect(() => {
    if (props.companyInfo.state === FetchState.NotFetched) {
      props.loadCompanyInfo();
    }
  }, [props.companyInfo.state, props.loadCompanyInfo]);

  // Clear input after successful save
  useEffect(() => {
    if (props.saveTokenState.state === FetchState.Success) {
      setTokenInput('');
    }
  }, [props.saveTokenState.state]);

  // Initialize collapse states based on token validity
  useEffect(() => {
    if (props.companyInfo.state === FetchState.Success) {
      const isInvalid = !props.companyInfo.data.hasValidVaphJwtToken;
      setShowInstructions(isInvalid);
      setShowTokenInput(isInvalid);
    }
  }, [props.companyInfo.state, props.companyInfo.data.hasValidVaphJwtToken]);

  const handleSave = () => {
    if (tokenInput.trim()) {
      props.saveVaphToken(tokenInput.trim());
    }
  };

  const handleClear = () => {
    setTokenInput('');
  };

  const handleCopyExample = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSnackbar(true);
    });
  };

  // Loading state
  if (props.companyInfo.state === FetchState.Busy || props.companyInfo.state === FetchState.NotFetched) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: 16 }}>
          {t('loadingCompanyInfo')}
        </Typography>
      </Box>
    );
  }

  // Error state
  if (props.companyInfo.state === FetchState.Error) {
    return (
      <ErrorMessage 
        title={t('errorLoadingCompanyInfo')} 
        message={tCommon('errorLoadingData')}
        errorDetails={props.companyInfo.errorMessage}
        options={[
          {
            optionLabel: tCommon('tryAgain'),
            optionExecute: props.loadCompanyInfo
          }
        ]} 
      />
    );
  }

  const hasValidToken = props.companyInfo.data.hasValidVaphJwtToken;

  return (
    <>
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          {t('vaphToken')}
        </Typography>
        
        {/* Company Info Section */}
        <Paper style={{ padding: 16, marginBottom: 24 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {t('companyName')}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {props.companyInfo.data.name}
          </Typography>
          
          <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 8 }}>
            {t('erkenningsnummer')}
          </Typography>
          <Typography variant="body1">
            {props.companyInfo.data.erkenningsnummer}
          </Typography>
        </Paper>

        {/* Token Status Section */}
        <Paper className={classes.statusSection} style={{ padding: 16 }}>
          <Typography variant="h6" gutterBottom>
            {t('vaphTokenStatus')}
          </Typography>
          
          {hasValidToken ? (
            <Chip 
              icon={<CheckCircleIcon />}
              label={t('hasValidToken')}
              color="primary"
              className={classes.statusChip}
            />
          ) : (
            <>
              <Chip 
                icon={<ErrorIcon />}
                label={t('noValidToken')}
                color="secondary"
                className={classes.statusChip}
              />
              <Alert severity="warning" className={classes.warningBox}>
                <Typography variant="body2">
                  <strong>{tCommon('pleaseNote')}</strong> {t('invalidTokenWarning')}
                </Typography>
              </Alert>
            </>
          )}
        </Paper>

        {/* Instructions Section */}
        <Paper className={classes.instructionsSection} style={{ padding: 16 }}>
          <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={() => {
            setShowInstructions(!showInstructions);
            if (!showInstructions) {
              setShowTokenInput(true);
            }
          }}>
            <InfoIcon color="primary" style={{ marginRight: 8 }} />
            <Typography variant="h6">
              {t('tokenInstructionsTitle')}
            </Typography>
          </Box>
          
          <Collapse in={showInstructions}>
            <Box mt={2}>
              <Typography variant="body2" paragraph>
                {t('tokenInstructionsIntro')}
              </Typography>
              
              <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                <strong>1. Inloggen</strong>
              </Typography>
              <Typography variant="body2" paragraph style={{ paddingLeft: 16 }}>
                • Ga naar <a href="https://www.mijnvaph.be" target="_blank" rel="noopener noreferrer">https://www.mijnvaph.be</a><br />
                • Log in als SE-medewerker
              </Typography>

              <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                <strong>2. Ga naar Tokens</strong>
              </Typography>
              <Typography variant="body2" paragraph style={{ paddingLeft: 16 }}>
                • Open het menu links<br />
                • Klik op <strong>Tokens</strong>
              </Typography>

              <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                <strong>3. Nieuw token toevoegen</strong>
              </Typography>
              <Box style={{ paddingLeft: 16 }}>
                <Typography variant="body2">
                  • Klik op <strong>Token toevoegen</strong>
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, marginBottom: 8 }}>
                  • Vul in:
                </Typography>
                <Box style={{ paddingLeft: 16 }}>
                  <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                    <Typography variant="body2" style={{ minWidth: 100 }}>
                      Naam:
                    </Typography>
                    <Chip
                      label={exampleName}
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopyExample(exampleName)}
                      onDelete={() => handleCopyExample(exampleName)}
                      deleteIcon={<FileCopyIcon />}
                      style={{ cursor: 'pointer' }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                    <Typography variant="body2" style={{ minWidth: 100 }}>
                      Vervaldatum:
                    </Typography>
                    <Chip
                      label={exampleExpiry}
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopyExample(exampleExpiry)}
                      onDelete={() => handleCopyExample(exampleExpiry)}
                      deleteIcon={<FileCopyIcon />}
                      style={{ cursor: 'pointer' }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                    <Typography variant="body2" style={{ minWidth: 100 }}>
                      Omschrijving:
                    </Typography>
                    <Chip
                      label={exampleDescription}
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopyExample(exampleDescription)}
                      onDelete={() => handleCopyExample(exampleDescription)}
                      deleteIcon={<FileCopyIcon />}
                      style={{ cursor: 'pointer' }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                    <Typography variant="body2" style={{ minWidth: 100 }}>
                      Rechten:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      minimaal GIR
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  • Klik op <strong>Verzenden</strong>
                </Typography>
              </Box>

              <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                <strong>4. Token kopiëren</strong>
              </Typography>
              <Typography variant="body2" paragraph style={{ paddingLeft: 16 }}>
                • Klik op <strong>Kopieer</strong><br />
                • Plak het token in het onderstaande veld
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                <strong>{tCommon('pleaseNote')}</strong> {t('tokenInstructionsSecurityWarning')}
              </Typography>
            </Box>
          </Collapse>
        </Paper>

        {/* Token Input Section */}
        <Paper className={classes.tokenInputSection} style={{ padding: 16 }}>
          <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={() => setShowTokenInput(!showTokenInput)}>
            <InfoIcon color="primary" style={{ marginRight: 8 }} />
            <Typography variant="h6">
              {t('saveVaphToken')}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            <strong>{tCommon('pleaseNote')}</strong> {t('tokenSecurityNotice')}
          </Typography>
          
          <Collapse in={showTokenInput}>
            <Box mt={2}>
              <TextField
                className={classes.tokenField}
                label={t('vaphToken')}
                placeholder={t('vaphTokenPlaceholder')}
                variant="outlined"
                multiline
                minRows={6}
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                disabled={props.saveTokenState.state === FetchState.Busy}
                helperText={t('tokenHelperText')}
              />
              
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={!tokenInput.trim() || props.saveTokenState.state === FetchState.Busy}
                >
                  {tCommon('save')}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  disabled={!tokenInput || props.saveTokenState.state === FetchState.Busy}
                >
                  {tCommon('cancel')}
                </Button>
              </div>
            </Box>
          </Collapse>
        </Paper>
      </Box>
      {props.saveTokenState.state === FetchState.Busy && (
        <SavingMessage entity={t('vaphToken')} />
      )}
      {props.saveTokenState.state === FetchState.Error && (
        <ErrorMessage 
          title={tCommon('savingErrorTitle')} 
          message={tCommon('savingErrorXMessage', { x: t('vaphToken') })} 
          errorDetails={props.saveTokenState.errorMessage}
          canClose 
          options={[]} 
        />
      )}
      <Snackbar
        open={copiedSnackbar}
        autoHideDuration={2000}
        onClose={() => setCopiedSnackbar(false)}
        message={tCommon('copiedToClipboard')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

// Export connected component
export const VaphToken = connect<IPropsFromState, IPropsFromDispatch, IProps, IApplicationState>(
  mapStateToProps,
  mapDispatchToProps
)(VaphTokenComponent);

export default VaphToken;

